import { Component, inject, OnDestroy, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { defaultIfEmpty, lastValueFrom, tap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import {
  AgentChatService,
  ConversationTurn,
} from '../services/agent-chat.service';

// Typewriter effect constants - a model can spit out a whole reply very quickly,
// (or also possibly a slowly..?)
// so we animate at an adaptive rate to keep the UI responsive.

// Interval between typewriter frames; sets the animation's frame rate.
const TYPEWRITER_TICK_MS = 25;
// Floor on chars revealed per tick so short backlogs still feel like typing.
const TYPEWRITER_MIN_CHARS_PER_TICK = 2;
// Target time (in ticks) to drain the current backlog. 60 ticks * 25ms = 1.5s.
const TYPEWRITER_DRAIN_TICKS = 60;

@Component({
  selector: 'app-agent-drawer',
  imports: [ButtonModule, FormsModule],
  templateUrl: './agent-drawer.component.html',
  styleUrl: './agent-drawer.component.scss',
})
export class AgentDrawerComponent implements OnDestroy {
  private agentChat = inject(AgentChatService);

  /**
   * New ID per component instance - a page refresh starts a new conversation.
   */
  private readonly adkSessionId = crypto.randomUUID();

  messageText = '';
  turns = signal<ConversationTurn[]>([]);
  /**
   * True for the full lifetime of a send - from submit until the typewriter
   * has drained the final response. Disables the form and guards `send()`
   * against re-entrancy.
   */
  loading = signal(false);
  /**
   * True between submit and the first streamed token. Drives the "Thinking…"
   * hint - models can think for ~30s before emitting.
   */
  waitingForFirstChunk = signal(false);
  error = signal<string | null>(null);

  // Variables to control the typewriter effect.
  private accumulatedText = '';
  private revealedLength = 0;
  private revealTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Pushes the user message plus an empty assistant placeholder into `turns` array.
   * Calls agent service to POST prompt, and manages the response through the 'typewriter'.
   */
  async send(): Promise<void> {
    const text = this.messageText.trim();
    if (!text || this.loading()) {
      return;
    }
    this.loading.set(true);
    this.waitingForFirstChunk.set(true);
    this.error.set(null);
    this.resetReveal();
    this.turns.update((t) => [
      ...t,
      { role: 'user', text },
      { role: 'assistant', text: '' },
    ]);
    this.messageText = '';
    try {
      await lastValueFrom(
        this.agentChat.sendPrompt(text, this.adkSessionId).pipe(
          tap((accumulated) => this.feedTypewriter(accumulated)),
          defaultIfEmpty(''),
        ),
      );
      await this.drainReveal();
    } catch (err: unknown) {
      console.error('Agent message failed', err);
      this.error.set('Failed to get a reply from the agent.');
      this.turns.update((rows) => {
        if (rows.length === 0) {
          return rows;
        }
        const next = [...rows];
        next[next.length - 1] = {
          role: 'assistant',
          text: '(Error)',
        };
        return next;
      });
    } finally {
      this.stopReveal();
      this.loading.set(false);
      this.waitingForFirstChunk.set(false);
    }
  }

  private feedTypewriter(accumulated: string): void {
    if (accumulated && this.waitingForFirstChunk()) {
      this.waitingForFirstChunk.set(false);
    }
    this.accumulatedText = accumulated;
    if (this.revealedLength > this.accumulatedText.length) {
      this.revealedLength = this.accumulatedText.length;
    }
    this.startRevealIfIdle();
  }

  private resetReveal(): void {
    this.stopReveal();
    this.accumulatedText = '';
    this.revealedLength = 0;
  }

  private startRevealIfIdle(): void {
    if (this.revealTimer !== null) {
      return;
    }
    this.revealTimer = setInterval(() => this.revealTick(), TYPEWRITER_TICK_MS);
  }

  /**
   * One typewriter step. Step size is adaptive: at least MIN_CHARS_PER_TICK
   * so short replies feel typed, otherwise `ceil(remaining / DRAIN_TICKS)`
   * so long backlogs drain in ~1.5s. Stops the interval once caught up.
   */
  private revealTick(): void {
    const remaining = this.accumulatedText.length - this.revealedLength;
    if (remaining <= 0) {
      this.stopReveal();
      return;
    }
    const charsThisTick = Math.max(
      TYPEWRITER_MIN_CHARS_PER_TICK,
      Math.ceil(remaining / TYPEWRITER_DRAIN_TICKS),
    );
    this.revealedLength = Math.min(
      this.revealedLength + charsThisTick,
      this.accumulatedText.length,
    );
    this.renderRevealed();
  }

  /**
   * Writes a slice of the accumulated text into the text field of the latest assistent turn.
   * Falls back to `…` so the bubble keeps height before the first token arrives.
   */
  private renderRevealed(): void {
    const visible = this.accumulatedText.slice(0, this.revealedLength);
    this.turns.update((rows) => {
      if (rows.length === 0) {
        return rows;
      }
      const next = [...rows];
      next[next.length - 1] = {
        role: 'assistant',
        text: visible || '…',
      };
      return next;
    });
  }

  /** Cancels the interval and clears the handle so `startRevealIfIdle` can restart. */
  private stopReveal(): void {
    if (this.revealTimer !== null) {
      clearInterval(this.revealTimer);
      this.revealTimer = null;
    }
  }

  /**
   * Called once the component has received the final chunk from the agent service.
   * Creates a promise that resolves once the visible text catches `revealTarget`.
   * i.e. once the typewriter has drained the final response.
   */

  private drainReveal(): Promise<void> {
    return new Promise((resolve) => {
      const check = (): void => {
        if (this.revealedLength >= this.accumulatedText.length) {
          resolve();
          return;
        }
        setTimeout(check, TYPEWRITER_TICK_MS);
      };
      check();
    });
  }

  /**
   * Keydown handler for the message textarea: plain Enter submits, Shift+Enter
   * (and other modifier combinations) falls through to the browser's default
   * newline insertion.
   */
  onEnterKey(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (
      keyboardEvent.shiftKey ||
      keyboardEvent.ctrlKey ||
      keyboardEvent.altKey ||
      keyboardEvent.metaKey ||
      keyboardEvent.isComposing
    ) {
      return;
    }
    event.preventDefault();
    void this.send();
  }

  ngOnDestroy(): void {
    this.stopReveal();
  }
}
